// Series primitive that draws the O'Neil-lineage chart markup around a VCP
// pivot (docs/projects/MODEL_BOOK_EVOLUTION.md §3.1 and §6):
//
//   - the three Key Price Range bands anchored on the pivot, from the pivot
//     day to the right edge: profit target (+20% to +25%, green), buy zone
//     (0 to +5%, blue) and loss range (−5% to −8%, pink — or up from the
//     base low where that sits inside the range);
//   - a solid zigzag polyline through the detector's contraction peak/trough
//     pairs with the depth of each leg written on it, so "each pullback
//     smaller than the last" is visible rather than inferred from dots;
//   - the `% to pivot` / `% from pivot` readout top-right: body colour while
//     the base is still forming, blue at or above the pivot once cleared,
//     magenta below it, with the time elapsed since the clearance.
//
// Everything here is derived from persisted detector geometry, so every line
// is SOLID (provenance by line style: dashed belongs to AI-proposed marks —
// vcp_annotation_marks_overlay.js — and a distinct style stays reserved for
// human-authored ones).
//
// Nothing here reads an outcome field. The bands, polyline and readout are
// computed from the bars the payload carries and the pivot, which is why the
// golden-label bench (a series truncated at peak_rating_date) gets the same
// drawing without being told how the setup resolved.
//
// Coordinates go through timeScale().timeToCoordinate() and
// series.priceToCoordinate(); logicalToCoordinate() silently returns 0 for
// fractional logicals. Text has no built-in primitive, so it is fillText in
// the renderer. Attached via series.attachPrimitive(...) from
// vcp_annotations_controller.

import { cssVar } from "./chart_theme"
import { findIndexAtOrAfter } from "./power_trend_background"

// `ink` / `inkDark` are the label colours on the light and dark themes: the
// light inks vanish against a dark page. The buy zone's top is not here: it
// is the detector's MAX_PCT_BELOW_PIVOT, sent as overlay.buy_zone_pct, so
// the drawn band is the scanner's own definition of "actionable".
export const BAND_SPECS = Object.freeze({
  target: { from: 0.20, to: 0.25, fill: "rgba(76, 175, 80, 0.14)",  ink: "#2e7d32", inkDark: "#81c784", label: "Target +20–25%" },
  buy:    { from: 0.00,           fill: "rgba(33, 150, 243, 0.16)", ink: "#1565c0", inkDark: "#64b5f6" },
  loss:   { from: -0.08, to: -0.05, fill: "rgba(233, 30, 99, 0.14)", ink: "#ad1457", inkDark: "#f06292", label: "Loss −5–8%" }
})
export const DEFAULT_BUY_ZONE_PCT = 0.05

export const READOUT_COLORS = Object.freeze({
  above: { light: "#1976d2", dark: "#64b5f6" },   // blue: at or above the pivot
  below: { light: "#d81b60", dark: "#f06292" }    // magenta: below it
})

// The theme controller sets data-bs-theme on <html>; charts read it the
// same way chart_theme.js reads the Bootstrap variables.
export function darkTheme() {
  return typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-bs-theme") === "dark"
}

// The page colours the renderers need, read once per attach / theme change.
export function readTheme() {
  return {
    dark: darkTheme(),
    background: cssVar("--bs-body-bg", "#ffffff"),
    text: cssVar("--bs-body-color", "#000000")
  }
}

const POLYLINE_COLORS = Object.freeze({ light: "#7e57c2", dark: "#b39ddb" })
// Exported so every VCP primitive measures its labels in the font drawLabel
// draws them in.
export const FONT = "600 11px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
const READOUT_FONT = "700 12px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
// The readout's right inset, and the widest text it can show ("−22.6% from
// pivot · 16w" is typical; this bounds it). Other primitives sharing the
// readout's row (the base summary) stop short of it using readoutReserve.
const READOUT_RIGHT_INSET_PX = 8
const READOUT_WIDEST_TEXT = "−00.0% from pivot · 00w"
const READOUT_GAP_PX = 12

// How much of the right edge of the top row the % to/from pivot readout
// claims, in media pixels, measured in its own font.
export function readoutReserve(ctx) {
  const previous = ctx.font
  ctx.font = READOUT_FONT
  const width = ctx.measureText(READOUT_WIDEST_TEXT).width
  ctx.font = previous
  return READOUT_RIGHT_INSET_PX + width + READOUT_GAP_PX
}

// The three bands as price ranges. The loss range floor is the base low when
// it sits inside the −5%..−8% range (a tighter stop than the convention);
// otherwise the convention's −8%.
export function pivotBands(pivot, baseLow, buyZonePct = DEFAULT_BUY_ZONE_PCT) {
  const p = Number(pivot)
  if (!(p > 0)) return []

  const low = Number(baseLow)
  const lossFloorDefault = p * (1 + BAND_SPECS.loss.from)
  const lossCeiling = p * (1 + BAND_SPECS.loss.to)
  const useBaseLow = low > lossFloorDefault && low < lossCeiling
  const buyTop = Number(buyZonePct) > 0 ? Number(buyZonePct) : DEFAULT_BUY_ZONE_PCT

  return [
    { key: "target", low: p * (1 + BAND_SPECS.target.from), high: p * (1 + BAND_SPECS.target.to), label: BAND_SPECS.target.label },
    { key: "buy",    low: p * (1 + BAND_SPECS.buy.from),    high: p * (1 + buyTop),
      label: `Buy zone 0–${(buyTop * 100).toFixed(0)}%` },
    { key: "loss",   low: useBaseLow ? low : lossFloorDefault, high: lossCeiling,
      label: useBaseLow ? `Loss to base low $${low.toFixed(2)}` : BAND_SPECS.loss.label }
  ]
}

// Alternating peak/trough vertices for the contraction polyline, with the
// leg depth carried on the trough vertex (that leg is the contraction).
export function contractionPath(contractions) {
  if (!Array.isArray(contractions)) return []
  const points = []
  contractions.forEach(c => {
    const peak = Number(c.peak), trough = Number(c.trough)
    if (c.peak_date && peak > 0) points.push({ time: c.peak_date, price: peak })
    if (c.trough_date && trough > 0) {
      const depth = Number(c.depth_pct)
      points.push({ time: c.trough_date, price: trough, legLabel: depth > 0 ? `−${(depth * 100).toFixed(0)}%` : null })
    }
  })
  return points
}

// `% to pivot` while the last close is under the pivot and no bar after the
// pivot day has closed at or above it; otherwise `% from pivot` measured at
// the last bar, plus the bars elapsed since the first close through the
// pivot. "At or above" is the cleared state everywhere here, so a close
// exactly on the pivot reads the same on every bar, and a last bar sitting
// above the pivot is never reported as a distance still to travel.
export function pivotReadout(data, pivot, pivotTime) {
  const p = Number(pivot)
  if (!(p > 0) || !Array.isArray(data) || data.length === 0) return null

  const last = data[data.length - 1]
  if (!last || !(Number(last.close) > 0)) return null

  const pct = (Number(last.close) - p) / p * 100
  const after = pivotTime ? data.filter(bar => bar.time > pivotTime) : data
  const crossIdx = after.findIndex(bar => Number(bar.close) >= p)

  if (crossIdx === -1 && pct < 0) {
    return { phase: "forming", pct, text: `${Math.abs(pct).toFixed(1)}% to pivot` }
  }

  const barsSince = crossIdx === -1 ? 0 : after.length - 1 - crossIdx
  const elapsed = barsSince < 5 ? `${barsSince}d` : `${Math.round(barsSince / 5)}w`
  const sign = pct >= 0 ? "+" : "−"
  return {
    phase: pct >= 0 ? "above" : "below",
    pct,
    barsSince,
    text: `${sign}${Math.abs(pct).toFixed(1)}% from pivot · ${elapsed}`
  }
}

// Inset either side of a label inside its region.
export const LABEL_INSET_PX = 4

// Whether `text` fits inside a region `width` wide, measured in the font it
// will be drawn in. Shared by every VCP primitive that labels a region. A fixed minimum width cannot answer this: the labels run
// from ~85px ("Edge vol 0.62×") to ~130px ("Run-up +74% vs market"), so any
// one number either hides short labels needlessly or lets long ones spill —
// off the pane's right edge, or across the base band's own summary.
export function labelFits(ctx, text, width) {
  ctx.font = FONT
  return ctx.measureText(text).width + 2 * LABEL_INSET_PX <= width
}

// Haloed text: a stroke in the page background under the fill so a label
// stays legible over candles in either theme. Exported so the annotation-mark
// primitive labels its marks identically rather than keeping a second copy.
export function drawLabel(ctx, text, x, y, color, background, font = FONT, align = "left") {
  ctx.font = font
  ctx.textAlign = align
  ctx.textBaseline = "middle"
  ctx.lineWidth = 3
  ctx.lineJoin = "round"
  ctx.strokeStyle = background
  ctx.strokeText(text, x, y)
  ctx.fillStyle = color
  ctx.fillText(text, x, y)
}

class BandsRenderer {
  constructor(data) { this._data = data }

  draw(target) {
    const { chart, series, bands, pivotTime, theme } = this._data
    if (!chart || !series || !bands || bands.length === 0 || !pivotTime) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const width = scope.mediaSize.width
      const timeScale = chart.timeScale()
      // null means the pivot day is not a bar on the scale at all; drawing
      // from x=0 would assert a buy zone across the whole history, so skip.
      const anchorX = timeScale.timeToCoordinate(pivotTime)
      if (anchorX === null) return
      const x0 = Math.max(0, anchorX)
      if (x0 >= width) return
      const { background, dark } = theme

      bands.forEach(band => {
        const yTop = series.priceToCoordinate(band.high)
        const yBottom = series.priceToCoordinate(band.low)
        if (yTop === null || yBottom === null) return
        const spec = BAND_SPECS[band.key]
        ctx.fillStyle = spec.fill
        ctx.fillRect(x0, yTop, width - x0, yBottom - yTop)
        // Labels sit at the right edge, clear of the pivot-day markers that
        // cluster where the bands begin.
        if (yBottom - yTop >= 12) {
          drawLabel(ctx, band.label, width - 6, yTop + 8, dark ? spec.inkDark : spec.ink, background, FONT, "right")
        }
      })
    })
  }
}

class MarksRenderer {
  constructor(data) { this._data = data }

  draw(target) {
    const { chart, series, path, readout, theme } = this._data
    if (!chart || !series) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const timeScale = chart.timeScale()

      this._drawPolyline(ctx, timeScale, series, path, theme)
      this._drawReadout(ctx, scope.mediaSize.width, readout, theme)
    })
  }

  _drawPolyline(ctx, timeScale, series, path, theme) {
    if (!path || path.length < 2) return
    const { background } = theme
    const ink = POLYLINE_COLORS[theme.dark ? "dark" : "light"]

    const coords = path.map(pt => {
      const x = timeScale.timeToCoordinate(pt.time)
      const y = series.priceToCoordinate(pt.price)
      return x === null || y === null ? null : { x, y, legLabel: pt.legLabel }
    })

    ctx.strokeStyle = ink
    ctx.lineWidth = 1.5
    ctx.setLineDash([])
    ctx.beginPath()
    let started = false
    coords.forEach(c => {
      if (!c) { started = false; return }
      if (started) ctx.lineTo(c.x, c.y)
      else ctx.moveTo(c.x, c.y)
      started = true
    })
    ctx.stroke()

    // Leg depth labels: on the trough vertex's leg, midway, nudged right so
    // the text sits beside the falling line rather than across it.
    for (let i = 1; i < coords.length; i++) {
      const from = coords[i - 1], to = coords[i]
      if (!from || !to || !to.legLabel) continue
      drawLabel(ctx, to.legLabel, (from.x + to.x) / 2 + 6, (from.y + to.y) / 2, ink, background)
    }
  }

  _drawReadout(ctx, width, readout, theme) {
    if (!readout) return
    const color = readout.phase === "forming"
      ? theme.text
      : READOUT_COLORS[readout.phase][theme.dark ? "dark" : "light"]
    drawLabel(ctx, readout.text, width - READOUT_RIGHT_INSET_PX, 12, color, theme.background, READOUT_FONT, "right")
  }
}

class PaneView {
  constructor(data, RendererClass, zOrder) {
    this._data = data
    this._RendererClass = RendererClass
    this._zOrder = zOrder
  }

  update(data) { this._data = data }
  renderer() { return new this._RendererClass(this._data) }
  zOrder() { return this._zOrder }
}

export class VcpMarkupOverlay {
  // @param pivotPrice  the setup's pivot
  // @param pivotTime   ISO date the bands start from (the pivot day, i.e. the
  //                    final contraction's peak; base_end as a fallback)
  // @param baseLow     the base's intraday low, or null
  // @param buyZonePct  the buy zone's top as a fraction (overlay.buy_zone_pct)
  // @param contractions the detector's contraction list
  // @param data        the bars on the chart (for the readout and the
  //                    autoscale range)
  constructor({ pivotPrice, pivotTime, baseLow, buyZonePct, contractions, data }) {
    const bars = data || []
    this._sharedData = {
      chart: null,
      series: null,
      pivotTime: pivotTime || null,
      // Logical index of the first bar on or after the pivot day, for
      // autoscaleInfo's visible-range test.
      pivotIndex: pivotTime ? findIndexAtOrAfter(bars, pivotTime) : 0,
      bands: pivotBands(pivotPrice, baseLow, buyZonePct),
      path: contractionPath(contractions),
      readout: pivotReadout(bars, pivotPrice, pivotTime),
      theme: readTheme()
    }
    this._paneViews = [
      new PaneView(this._sharedData, BandsRenderer, "bottom"),
      new PaneView(this._sharedData, MarksRenderer, "top")
    ]
    this._requestUpdate = null
    this._onThemeChanged = () => {
      this._sharedData.theme = readTheme()
      this.updateAllViews()
      if (this._requestUpdate) this._requestUpdate()
    }
  }

  attached(param) {
    this._sharedData.chart = param.chart
    this._sharedData.series = param.series
    this._requestUpdate = param.requestUpdate
    // Colours are read once here and again on theme:changed, not on every
    // frame: getComputedStyle inside a renderer runs twice per pan/zoom tick.
    this._sharedData.theme = readTheme()
    document.addEventListener("theme:changed", this._onThemeChanged)
    this.updateAllViews()
    param.requestUpdate()
  }

  detached() {
    document.removeEventListener("theme:changed", this._onThemeChanged)
    this._requestUpdate = null
    this._sharedData.chart = null
    this._sharedData.series = null
  }

  updateAllViews() {
    this._paneViews.forEach(pv => pv.update(this._sharedData))
  }

  paneViews() {
    return this._paneViews
  }

  // Keep the target and loss bands on screen: without this a tight base
  // autoscaled to its own range would clip the +25% band off the top. Only
  // while the pivot day is inside the visible range, though — scrolled back
  // into the history where no band is drawn, the candles get the full scale.
  autoscaleInfo(startTimePoint, endTimePoint) {
    const { bands, pivotIndex } = this._sharedData
    if (!bands || bands.length === 0) return null
    if (typeof endTimePoint === "number" && pivotIndex > endTimePoint) return null
    return {
      priceRange: {
        minValue: Math.min(...bands.map(b => b.low)),
        maxValue: Math.max(...bands.map(b => b.high))
      }
    }
  }
}
